import name from 'forms/shared/validators/models/name/name';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import locationUSCityStateZipInternationalCityModelValidator from 'forms/shared/validators/models/location/models/usCityStateZipInternationalCity';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { DEFAULT_EARLIEST, DEFAULT_LATEST, OTHER } from 'constants/dateLimits';
import { sortSafeDateTimes, getSafeDateTime } from 'helpers/date';
import { previouslyMarriedOptions } from 'constants/enums/relationshipOptions';
import { ValidateJSModel } from 'forms/shared/types';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import locationBirthplaceWithoutCountyModelValidator from 'forms/shared/validators/models/location/models/birthplaceWithoutCounty';
import { isPVQForm } from 'helpers/form';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import locationBirthplaceModelValidator from 'forms/shared/validators/models/location/models/birthplace';
import { hasYesOrNo } from 'models/validate';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { RelationshipsDivorcedItemModel } from '../types';

export const relationshipsMaritalDivorceItemValidator: ValidateJSModel<RelationshipsDivorcedItemModel> = {
  DeceasedAddressNotApplicable: {},

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator },
      };
    }
    return {
      presence: true,
      model: { validator: name },
    };
  },

  Birthdate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus === 'Widowed') {
        return {};
      }

      if (attributes.DateDivorced?.month !== '') {
        return {
          presence: true,
          date: {
            earliest: DEFAULT_EARLIEST,
            latest: DEFAULT_LATEST,
            tooEarly: 'error.birthdateTooFar.min'
          }
        };
      }

      return {};
    }

    return {
      presence: true,
      date: { ...OTHER, tooEarly: 'error.birthdateTooFar.min' },
    };
  },

  BirthPlace: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) {
        return {
          presence: true,
          location: {
            validator: locationBirthplaceModelValidator,
          },
        };
      }

      return {};
    }

    return {
      presence: true,
      location: { validator: locationBirthplaceWithoutCountyModelValidator },
    };
  },

  Citizenship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) {
        return {
          hasValue: true,
          country: true,
        };
      }
      return {};
    }

    return {
      presence: true,
      country: true,
      hasValue: true
    };
  },

  Deceased: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) {
        return {
          presence: true,
          hasValue: { validator: hasYesOrNo },
        };
      }
      return {};
    }

    if (attributes?.Status?.value === 'Widowed') { return {}; }

    return {
      hasValue: {
        validator: { inclusion: ['Yes', 'No', 'DK'] },
      },
    };
  },

  Telephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if ((options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) && attributes?.Deceased?.value === 'No') {
        return {
          presence: true,
          model: {
            validator: phoneModelValidator,
            requireNumberType: true
          },
        };
      }

      return {};
    }

    if (options?.requireRelationshipMaritalDivorcePhoneNumber) {
      return {
        presence: true,
        model: { validator: phoneModelValidator },
      };
    }
    return {};
  },

  Emails: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && (options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0)
      && attributes?.Deceased?.value === 'No') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
              },
            },
          },
        },
      };
    }

    return {};
  },

  Recognized: (value, attributes, attributeName, options) => {
    const { Birthdate } = attributes;
    const { applicantBirthdate } = options;

    const sortedDates = sortSafeDateTimes([
      getSafeDateTime(Birthdate),
      getSafeDateTime(applicantBirthdate),
    ]);

    const earliestDate = sortedDates.length ? sortedDates[sortedDates.length - 1] : undefined;

    if (isPVQForm(options?.formType)) {
      if ((options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) && attributes?.Deceased?.value === 'No') {
        return {
          presence: true,
          date: {
            earliest: earliestDate?.datetime,
            latest: DEFAULT_LATEST,
            tooEarly: 'error.date.invalid',
          },
        };
      }

      return {};
    }

    return {
      presence: true,
      date: {
        earliest: earliestDate?.datetime,
        latest: DEFAULT_LATEST,
      },
    };
  },

  Address: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return {
      presence: true,
      location: { validator: locationBirthplaceWithoutCountyModelValidator },
    };
  },

  DateDivorced: (value, attributes, attributeName, options) => {
    const dateLimits: DateBaseValidatorOptions = { latest: DEFAULT_LATEST };
    if (attributes?.Recognized) {
      dateLimits.earliest = attributes?.Recognized;
    }
    dateLimits.tooEarly = 'error.divorceDate.min';
    if (isPVQForm(options?.formType)) {
      if ((options?.maritalStatus !== 'Widowed' || attributes?.$index !== 0) && attributes?.Deceased?.value === 'No') {
        return {
          presence: true,
          date: dateLimits,
        };
      }

      return {};
    }
    return {
      presence: true,
      date: dateLimits,
    };
  },

  DeceasedAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      // PVQ does not have a deceased address it needs for any purposes.
      return {};
    }

    if (attributes?.DeceasedAddressNotApplicable?.applicable === false) {
      return {};
    }

    if (attributes?.Status?.value === 'Widowed') { return {}; }

    if (attributes?.Deceased?.value !== 'No') { return {}; }

    return {
      presence: true,
      location: { validator: locationAddressModelValidator },
    };
  },

  DeceasedDate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (
        options?.maritalStatus === 'Widowed'
      ) {
        const dateLimits: DateBaseValidatorOptions = { latest: DEFAULT_LATEST };

        return {
          presence: true,
          date: dateLimits,
        };
      }
    }

    return {};
  },

  Status: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return {
      hasValue: {
        validator: { inclusion: previouslyMarriedOptions },
      },
    };
  },

  DivorceLocation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (
        attributes?.Deceased?.value !== 'Yes'
        && options.parentAttributes?.DivorcedList?.items.length > 0
        && attributes?.DivorceLocationNotApplicable?.applicable !== false) {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: true,
            requireCity: true,
            requireIsMilitaryDiplomaticAddress: true,
            requireMilitaryDiplomaticInstallationName: true,
            requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          }
        };
      }

      return {};
    }
    if (['Divorced', 'Annulled'].includes(attributes?.Status?.value || '')) {
      return {
        presence: true,
        location: { validator: locationUSCityStateZipInternationalCityModelValidator },
      };
    }
    return {};
  },

  DivorceLocationNotApplicable: {},

  MarriageLocation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.Deceased?.value !== 'Yes' && ['Divorced', 'Annulled'].includes(options?.maritalStatus || '')) {
        return {
          presence: true,
          location: {
            validator: locationBirthplaceModelValidator,
          },
        };
      }
    }
    return {};
  },

  Comments: {},
  DivorcedAnnulledComments: {},
  DivorcedAnnulledComments2: {},
};
